import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { __ } from '@wordpress/i18n';

// components
import AiJobList from '../AiJobList';
import ModalDialog from '../ModalDialog';

// icons
import DialogueLayerIcon from '../../icons/DialogueLayerIcon';
import MusicLayerIcon from '../../icons/MusicLayerIcon';
import SfxLayerIcon from '../../icons/SfxLayerIcon';
import BassIcon from '../../icons/BassIcon';
import DrumsIcon from '../../icons/DrumsIcon';
import GuitarIcon from '../../icons/GuitarIcon';
import OtherInstrumentIcon from '../../icons/OtherInstrumentIcon';
import PercussionIcon from '../../icons/PercussionIcon';
import StringsIcon from '../../icons/StringsIcon';
import WoodwindIcon from '../../icons/WoodwindIcon';
import PianoIcon from '../../icons/PianoIcon';
import MagicAiIcon from '../../icons/MagicAiIcon';

// hooks
import useAiJobs from '../../hooks/useAiJobs';

// service
import StemService from '../../service/stem-service';
import GenerationService from '../../service/generation-service';

import '../../assets/components/ModalStemSplitter.css';
import '../../assets/components/ModalMoodCover.css';

const STUDIO_TYPES = [
    { id: 'dialogue', label: 'Dialogue', Icon: DialogueLayerIcon },
    { id: 'music',    label: 'Music',    Icon: MusicLayerIcon },
    { id: 'sfx',      label: 'SFX',      Icon: SfxLayerIcon },
];

const REC_TYPES = [
    { id: 'bass',       label: 'Bass',       Icon: BassIcon },
    { id: 'drums',      label: 'Drums',      Icon: DrumsIcon },
    { id: 'guitar',     label: 'Guitar',     Icon: GuitarIcon },
    { id: 'percussion', label: 'Percussion', Icon: PercussionIcon },
    { id: 'piano',      label: 'Piano',      Icon: PianoIcon },
    { id: 'strings',    label: 'Strings',    Icon: StringsIcon },
    { id: 'woodwind',   label: 'Woodwind',   Icon: WoodwindIcon },
    { id: 'other',      label: 'Other',      Icon: OtherInstrumentIcon },
];

/** Which layers a stem request asked for, for the row label. */
const stemLabel = (job) => [
    job.music && 'Music',
    job.sfx && 'SFX',
    job.dialogue && 'Dialogue',
].filter(Boolean).join(' + ') || __('Stem', 'soundbetween');

/**
 * Stem splitter (Moises) and "Genera strumento" (Kits.ai) share this modal: both
 * pick layers off a source clip and queue an asynchronous job.
 *
 * The request is fire and forget on the API side — no webhook, no job GET — so
 * the queue below the form polls the list endpoint until everything settles.
 *
 * @param {object}   props
 * @param {string}   props.kind      'stems' | 'generations'
 * @param {function} props.onSubmit  builds and sends the request; may be async
 */
const ModalStemSplitter = ({
    visible,
    selection,
    sources = [],
    projectId,
    kind = 'stems',
    onHide,
    onSubmit,
    title = 'Stem Splitter',
}) => {
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [note, setNote] = useState('');
    const [sourceKey, setSourceKey] = useState('');
    // The stem list carries no consent flag yet, so a toggle is remembered here
    // for the session once it has been set.
    const [consents, setConsents] = useState({});

    const isStems = kind === 'stems';
    const types = selection?.timelineType === 'rec' ? REC_TYPES : STUDIO_TYPES;

    const { jobs, isFetching, waiting, refresh } = useAiJobs({
        queryKey: ['project', projectId, kind],
        queryFn: () => (isStems
            ? StemService.listStemsAsync(projectId)
            : GenerationService.listGenerationsAsync(projectId)),
        enabled: Boolean(visible && projectId),
        select: useCallback(
            (list) => (isStems ? list : list.filter(g => g.mode === 'instrument')),
            [isStems]
        ),
    });

    const submit = useMutation({
        mutationFn: (payload) => onSubmit(payload),
        onSuccess: () => {
            setSelectedTypes([]);
            setNote('');
            refresh();
        },
    });

    const consent = useMutation({
        mutationFn: ({ stemId, value }) => StemService.setStemConsentAsync(stemId, value),
        onSuccess: (_data, { stemId, value }) => setConsents(prev => ({ ...prev, [stemId]: value })),
    });

    useEffect(() => {
        if (visible && sources.length && !sourceKey) {
            setSourceKey(`${sources[0].type}:${sources[0].id}`);
        }

        if (!visible) {
            setSourceKey('');
            setSelectedTypes([]);
            setNote('');
        }
    }, [visible, sources, sourceKey]);

    const toggleType = (id) => {
        setSelectedTypes(prev => (
            prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
        ));
    };

    const handleHide = () => {
        setSelectedTypes([]);
        setNote('');
        setSourceKey('');
        onHide();
    };

    const handleSubmit = () => {
        if (!selectedTypes.length || submit.isPending) return;

        let sourceType = null;
        let sourceId = null;

        if (sourceKey) {
            const idx = sourceKey.indexOf(':');
            sourceType = sourceKey.slice(0, idx);
            sourceId = sourceKey.slice(idx + 1);
        }

        submit.mutate({ ...selection, types: selectedTypes, note, sourceType, sourceId });
    };

    /**
     * Consent to publish the separated stems in the musician's public library.
     * Only makes sense once there is something to publish.
     */
    const renderConsent = (job) => {
        if (!isStems || job.status !== 'completed') return null;

        const checked = consents[job.id] ?? Boolean(job.stemConsent);

        return (
            <label className="stemSplitterModal__consent">
                <input
                    type="checkbox"
                    checked={checked}
                    disabled={consent.isPending}
                    onChange={(e) => consent.mutate({ stemId: job.id, value: e.target.checked })}
                />
                <span>{__('Rendi questi stem visibili nella libreria pubblica', 'soundbetween')}</span>
            </label>
        );
    };

    return (
        <ModalDialog
            header={title}
            visible={visible}
            onHide={handleHide}
            onSubmit={handleSubmit}
            width={480}>
            <div className="stemSplitterModal">
                {sources.length > 0 && (
                    <div className="stemSplitterModal__source">
                        <label className="stemSplitterModal__sourceLabel">
                            {__('Sorgente audio', 'soundbetween')}
                        </label>
                        <select
                            className="stemSplitterModal__sourceSelect"
                            value={sourceKey}
                            onChange={e => setSourceKey(e.target.value)}>
                            {sources.map(s => (
                                <option key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className={`stemSplitterModal__types ${selection?.timelineType === 'rec' ? 'twoCols' : 'onCol'}`}>
                    {types.map(({ id, label, Icon }) => (
                        <label
                            key={id}
                            className={`appButton__3d stemSplitterModal__typeBtn${selectedTypes.includes(id) ? ' stemSplitterModal__typeBtn--selected' : ''}`}>
                            <input
                                type="checkbox"
                                checked={selectedTypes.includes(id)}
                                onChange={() => toggleType(id)}
                            />
                            <span>
                                <Icon/>
                                {label}
                            </span>
                        </label>
                    ))}
                </div>
                <textarea
                    className="stemSplitterModal__note"
                    placeholder={__('Aggiungi una nota…', 'soundbetween')}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    rows={3}
                />
                <div className="stemSplitterModal__actions">
                    <button
                        className="stemSplitterModal__submit"
                        disabled={!selectedTypes.length || submit.isPending}
                        onClick={handleSubmit}>
                        <MagicAiIcon/>
                        {submit.isPending ? __('Invio…', 'soundbetween') : __('Genera', 'soundbetween')}
                    </button>
                </div>

                {projectId && (
                    <AiJobList
                        title={isStems
                            ? __('Separazioni del progetto', 'soundbetween')
                            : __('Strumenti generati', 'soundbetween')}
                        jobs={jobs}
                        loading={isFetching}
                        waiting={waiting}
                        onRefresh={refresh}
                        emptyLabel={isStems
                            ? __('Nessuna separazione ancora.', 'soundbetween')
                            : __('Nessuno strumento generato ancora.', 'soundbetween')}
                        renderLabel={(job) => (isStems
                            ? stemLabel(job)
                            : (job.instrument || __('Strumento', 'soundbetween')))}
                        renderExtra={renderConsent}
                    />
                )}
            </div>
        </ModalDialog>
    );
};

export default ModalStemSplitter;
